import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Download, DirectionsBus, People, Event } from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { RouteMonthlyCheckin, DailyCheckinData, CSVCheckinRecord } from '../types/boarding';
import { exportToCSV } from '../utils/csvExporter';
import { Route } from '../types/route';

const isReservationCheckin = (type?: string) => type === 'reservation';

export const BoardingPage: React.FC = () => {
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>('');
  const [checkinData, setCheckinData] = useState<RouteMonthlyCheckin[]>([]);
  const [routeNames, setRouteNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // 월 선택 옵션 생성 (최근 12개월)
  const monthOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = format(date, 'yyyyMM');
      const label = format(date, 'yyyy년 MM월', { locale: ko });
      options.push({ value: yearMonth, label });
    }

    return options;
  }, []);

  // 초기 선택: 현재 월
  useEffect(() => {
    if (monthOptions.length > 0 && !selectedYearMonth) {
      setSelectedYearMonth(monthOptions[0].value);
    }
  }, [monthOptions, selectedYearMonth]);

  // 월별 승차 데이터 조회
  useEffect(() => {
    const fetchCheckinData = async () => {
      if (!selectedYearMonth) return;

      setLoading(true);
      try {
        const q = query(
          collection(db, 'route_monthly_checkins'),
          where('year_month', '==', selectedYearMonth)
        );

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as RouteMonthlyCheckin[];

        setCheckinData(data);

        // 노선 ID에서 노선명 조회
        const routeNameMap: Record<string, string> = {};
        const uniqueRouteIds = new Set<string>();

        // line_name에서 routeId 추출 (예: 'routeId_YYYYMM' 형식에서 routeId 부분 추출)
        data.forEach((checkin) => {
          // id 형식이 'routeId_YYYYMM'이므로 '_'로 split하여 routeId 추출
          const routeId = checkin.id.split('_')[0];
          uniqueRouteIds.add(routeId);
        });

        // 각 노선 ID에 대해 routes 컬렉션에서 이름 조회
        const routeIdsArray = Array.from(uniqueRouteIds);
        for (const routeId of routeIdsArray) {
          try {
            const routeDoc = await getDoc(doc(db, 'routes', routeId));
            if (routeDoc.exists()) {
              const routeData = routeDoc.data() as Route;
              routeNameMap[routeId] = routeData.name;
            } else {
              routeNameMap[routeId] = routeId; // 노선을 찾을 수 없으면 ID 사용
            }
          } catch (error) {
            console.error(`Error fetching route ${routeId}:`, error);
            routeNameMap[routeId] = routeId; // 에러 발생 시 ID 사용
          }
        }

        setRouteNames(routeNameMap);
      } catch (error) {
        console.error('Error fetching checkin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckinData();
  }, [selectedYearMonth]);

  // 통계 계산
  const statistics = useMemo(() => {
    const totalCount = checkinData.reduce((sum, route) => sum + route.total_count, 0);
    const uniqueUsersSet = new Set<string>();

    checkinData.forEach((route) => {
      route.unique_users.forEach((uid) => uniqueUsersSet.add(uid));
    });

    return {
      totalCount,
      uniqueUsers: uniqueUsersSet.size,
    };
  }, [checkinData]);

  // 일별 승차 데이터 집계
  const dailyData = useMemo(() => {
    const dailyMap: Record<number, number> = {};

    // 1-31일 초기화
    for (let i = 1; i <= 31; i++) {
      dailyMap[i] = 0;
    }

    // 승차 기록 집계
    checkinData.forEach((route) => {
      route.checkins.forEach((checkin) => {
        const day = checkin.timestamp.toDate().getDate();
        dailyMap[day]++;
      });
    });

    // 차트 데이터로 변환
    const data: DailyCheckinData[] = [];
    for (let i = 1; i <= 31; i++) {
      data.push({ day: i, count: dailyMap[i] });
    }

    return data;
  }, [checkinData]);

  // CSV 다운로드
  const handleDownloadCSV = () => {
    if (checkinData.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // CSV 레코드 생성
    const csvRecords: CSVCheckinRecord[] = [];

    checkinData.forEach((route) => {
      // routeId 추출
      const routeId = route.id.split('_')[0];
      const routeName = routeNames[routeId] || route.line_name;

      route.checkins.forEach((checkin) => {
        csvRecords.push({
          line_name: routeName,
          boarding_type: isReservationCheckin(checkin.type) ? '예약승차' : 'QR승차',
          organization: checkin.organization,
          station_name: checkin.station_name,
          timestamp: format(checkin.timestamp.toDate(), 'yyyy-MM-dd HH:mm:ss'),
        });
      });
    });

    // 시간순 정렬
    csvRecords.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    // CSV 내보내기
    exportToCSV(
      csvRecords,
      [
        { key: 'line_name', header: '이용노선' },
        { key: 'boarding_type', header: '승차 유형' },
        { key: 'organization', header: '탑승자 소속' },
        { key: 'station_name', header: '이용 정류장' },
        { key: 'timestamp', header: '탑승 시간' },
      ],
      `승차기록_${selectedYearMonth}.csv`
    );
  };

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsBus sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                승차 정보
              </Typography>
              <Typography variant="body2" color="text.secondary">
                월별 승차 통계 및 이용 현황을 확인합니다.
              </Typography>
            </Box>
          </Box>

          {/* 월 선택 */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>조회 월</InputLabel>
            <Select
              value={selectedYearMonth}
              label="조회 월"
              onChange={(e) => setSelectedYearMonth(e.target.value)}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* 통계 카드 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Paper sx={{ flex: 1, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Event sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    총 승차 횟수
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {statistics.totalCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  선택한 월의 총 승차 횟수입니다.
                </Typography>
              </Paper>

              <Paper sx={{ flex: 1, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <People sx={{ fontSize: 32, color: '#764ba2' }} />
                  <Typography variant="h6" fontWeight={600}>
                    고유 사용자 수
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="secondary">
                  {statistics.uniqueUsers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  선택한 월에 이용한 고유 사용자 수입니다.
                </Typography>
              </Paper>
            </Box>

            {/* 일별 승차 현황 그래프 */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  일별 승차 현황
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={handleDownloadCSV}
                  disabled={checkinData.length === 0}
                >
                  CSV 다운로드
                </Button>
              </Box>

              {checkinData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">
                    선택한 월의 승차 기록이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="day"
                      label={{ value: '일', position: 'insideBottom', offset: -5 }}
                    />
                    <YAxis
                      label={{ value: '승차 횟수', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value}회`, '승차 횟수']}
                      labelFormatter={(label) => `${label}일`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#667eea"
                      strokeWidth={2}
                      name="승차 횟수"
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>

            {/* 노선별 승차 통계 */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                노선별 승차 통계
              </Typography>

              {checkinData.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    노선별 통계가 없습니다.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {checkinData
                    .map((route) => {
                      // routeId 추출
                      const routeId = route.id.split('_')[0];
                      const routeName = routeNames[routeId] || route.line_name;
                      const reservationCount = route.checkins.filter((checkin) =>
                        isReservationCheckin(checkin.type)
                      ).length;
                      const qrCount = route.checkins.length - reservationCount;

                      return { route, routeName, qrCount, reservationCount };
                    })
                    .sort((a, b) => a.routeName.localeCompare(b.routeName, 'ko'))
                    .map(({ route, routeName, qrCount, reservationCount }) => (
                      <Paper key={route.id} sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DirectionsBus sx={{ color: '#667eea' }} />
                            <Typography variant="body1" fontWeight={600}>
                              {routeName}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                승차 횟수
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="primary">
                                {route.total_count.toLocaleString()}회
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                QR승차
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="#0288d1">
                                {qrCount.toLocaleString()}회
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                예약승차
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="#ed6c02">
                                {reservationCount.toLocaleString()}회
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                고유 사용자
                              </Typography>
                              <Typography variant="h6" fontWeight={600} color="secondary">
                                {route.unique_users.length.toLocaleString()}명
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                </Box>
              )}
            </Paper>
          </>
        )}
      </Box>
    </MainLayout>
  );
};
